import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'

interface AuthGuardProps {
  children: ReactNode
}

/**
 * AuthGuard — protects all routes that require authentication.
 *
 * Behavior:
 * - While Clerk is loading the session: show a centered spinner.
 * - If not signed in: redirect to / (the landing page with embedded sign-in).
 * - If signed in: render children.
 *
 * This component is intentionally thin. Business-logic guards (e.g. role checks)
 * belong on the individual page or in a separate guard layer.
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const { isLoaded, isSignedIn } = useAuth()

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-brand-blue border-t-transparent"
          role="status"
          aria-label="Loading…"
        />
      </div>
    )
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />
  }

  return <>{children}</>
}
